import enumTypes, { mediaTypes } from '@/app/library/constants';
import { exhaustiveMap } from '@/utils';

export const PLATFORMS = {
  INSTAGRAM: 'instagram',
  TIKTOK: 'tiktok',
  FACEBOOK: 'facebook',
  PINTEREST: 'pinterest',
  YOUTUBE: 'youtube',
  LINKEDIN: 'linkedin',
  THREADS: 'threads',
  TWITTER: 'twitter',
  BLUESKY: 'bluesky',
  SNAPCHAT: 'snapchat',
};

export const ALL_PLATFORMS = Object.values(PLATFORMS);
export const AUTO_PUBLISH_ONLY_PLATFORMS = [
  PLATFORMS.LINKEDIN,
  PLATFORMS.THREADS,
  PLATFORMS.BLUESKY,
  PLATFORMS.YOUTUBE,
  PLATFORMS.SNAPCHAT,
];

export const DIALOG_TYPES = {
  FACEBOOK: 'facebook',
  INSTAGRAM: 'instagram',
  PINTEREST: 'pinterest',
  PINTEREST_BULK: 'pinterestBulk',
  TWITTER: 'twitter',
  TIKTOK: 'tiktok',
  LINKEDIN: 'linkedin',
  BLUESKY: 'bluesky',
  THREADS: 'threads',
  YOUTUBE: 'youtube',
  SNAPCHAT: 'snapchat',
};

export const DIALOG_TYPE_PLATFORMS = exhaustiveMap(DIALOG_TYPES, {
  [DIALOG_TYPES.FACEBOOK]: PLATFORMS.FACEBOOK,
  [DIALOG_TYPES.INSTAGRAM]: PLATFORMS.INSTAGRAM,
  [DIALOG_TYPES.PINTEREST]: PLATFORMS.PINTEREST,
  [DIALOG_TYPES.PINTEREST_BULK]: PLATFORMS.PINTEREST,
  [DIALOG_TYPES.TWITTER]: PLATFORMS.TWITTER,
  [DIALOG_TYPES.TIKTOK]: PLATFORMS.TIKTOK,
  [DIALOG_TYPES.LINKEDIN]: PLATFORMS.LINKEDIN,
  [DIALOG_TYPES.BLUESKY]: PLATFORMS.BLUESKY,
  [DIALOG_TYPES.THREADS]: PLATFORMS.THREADS,
  [DIALOG_TYPES.YOUTUBE]: PLATFORMS.YOUTUBE,
  [DIALOG_TYPES.SNAPCHAT]: PLATFORMS.SNAPCHAT,
});

export const MULTI_CHANNEL_POST_TYPES = {
  TIKTOK_POST: 'TIKTOK_POST',
  INSTAGRAM_POST: 'INSTAGRAM_POST',
  INSTAGRAM_STORY_FRAME: 'INSTAGRAM_STORY_FRAME',
  FACEBOOK_POST: 'FACEBOOK_POST',
  PINTEREST_PIN: 'PINTEREST_PIN',
  TWITTER_TWEET: 'TWITTER_TWEET',
  LINKEDIN_POST: 'LINKEDIN_POST',
  BLUESKY_POST: 'BLUESKY_POST',
  THREADS_POST: 'THREADS_POST',
  YOUTUBE_SHORT: 'YOUTUBE_SHORT',
  SNAPCHAT_POST: 'SNAPCHAT_POST',
  APPROVAL_PENDING_POST: 'APPROVAL_PENDING_POST',
};

export const MULTI_CHANNEL_POST_DESCRIPTIONS = exhaustiveMap(MULTI_CHANNEL_POST_TYPES, {
  TIKTOK_POST: 'Post',
  INSTAGRAM_POST: 'Post',
  INSTAGRAM_STORY_FRAME: 'Story frame',
  FACEBOOK_POST: 'Post',
  PINTEREST_PIN: 'Pin',
  TWITTER_TWEET: 'Post',
  LINKEDIN_POST: 'Post',
  BLUESKY_POST: 'Post',
  THREADS_POST: 'Post',
  YOUTUBE_SHORT: 'Short',
  SNAPCHAT_POST: 'Post',
  APPROVAL_PENDING_POST: 'Post',
});

export const MULTI_CHANNEL_POST_ICONS = exhaustiveMap(MULTI_CHANNEL_POST_TYPES, {
  TIKTOK_POST: 'tiktok-color',
  INSTAGRAM_POST: 'instagram-color',
  INSTAGRAM_STORY_FRAME: 'instagram-story-color',
  FACEBOOK_POST: 'facebook-color',
  PINTEREST_PIN: 'pinterest-color',
  TWITTER_TWEET: 'twitter-color',
  LINKEDIN_POST: 'linkedin-color',
  BLUESKY_POST: 'bluesky-color',
  THREADS_POST: 'threads-color',
  APPROVAL_PENDING_POST: 'message-check',
  YOUTUBE_SHORT: 'youtube-color',
  SNAPCHAT_POST: 'snapchat-color',
});

export const PLATFORMS_TO_MULTI_CHANNEL_POST_TYPES = exhaustiveMap(PLATFORMS, {
  tiktok: 'TIKTOK_POST',
  instagram: 'INSTAGRAM_POST',
  facebook: 'FACEBOOK_POST',
  pinterest: 'PINTEREST_PIN',
  twitter: 'TWITTER_TWEET',
  linkedin: 'LINKEDIN_POST',
  bluesky: 'BLUESKY_POST',
  threads: 'THREADS_POST',
  youtube: 'YOUTUBE_SHORT',
  snapchat: 'SNAPCHAT_POST',
});

export const textProps = exhaustiveMap(PLATFORMS, {
  facebook: 'message',
  instagram: 'caption',
  pinterest: 'note',
  twitter: 'tweet_status',
  tiktok: 'text',
  linkedin: 'text',
  bluesky: 'text',
  threads: 'text',
  youtube: 'description',
  snapchat: 'description',
});

export const platformLabels = exhaustiveMap(PLATFORMS, {
  facebook: 'Facebook',
  instagram: 'Instagram',
  pinterest: 'Pinterest',
  twitter: 'X',
  tiktok: 'TikTok',
  linkedin: 'LinkedIn',
  bluesky: 'Bluesky',
  threads: 'Threads',
  youtube: 'YouTube',
  snapchat: 'Snapchat',
});

export const shopProps = {
  facebook: 'productTags',
  instagram: 'shoppingTags',
};

export const postDescriptions = exhaustiveMap(PLATFORMS, {
  twitter: 'X Post',
  pinterest: 'Pinterest pin',
  instagram: 'Instagram post',
  facebook: 'Facebook post',
  tiktok: 'TikTok post',
  linkedin: 'LinkedIn post',
  bluesky: 'Bluesky post',
  threads: 'Threads post',
  youtube: 'YouTube post',
  snapchat: 'Snapchat post',
});

export const postTypes = {
  FEED: 'FEED',
  STORY: 'STORY',
  REEL: 'REEL',
};

export const fbPostTypes = {
  POST: 'POST',
  REEL: 'REEL',
};

export const FB_POST_PREVIEW_TYPES = {
  TEXT: 'text',
  IMAGE: 'image',
  VIDEO: 'video',
  LINK: 'link',
};

export const X_POST_PREVIEW_TYPES = {
  TEXT: 'text',
  IMAGE: 'image',
  VIDEO: 'video',
};

export const snapchatPostTypes = {
  STORY: 'STORY',
  SPOTLIGHT: 'SPOTLIGHT',
};

export const PLATFORM_POST_TYPE = {
  FACEBOOK_POST: 'facebook_post',
  INSTAGRAM_REEL: 'instagram_reel',
  INSTAGRAM_STORY: 'instagram_story',
  INSTAGRAM_POST: 'instagram_post',
  PINTEREST_PIN: 'pinterest_pin',
  TIKTOK_POST: 'tiktok_post',
  TWITTER_POST: 'twitter_post',
  LINKEDIN_POST: 'linkedin_post',
  BLUESKY_POST: 'bluesky_post',
  THREADS_POST: 'threads_post',
  YOUTUBE_SHORT: 'youtube_short',
  SNAPCHAT_POST: 'snapchat_post',
};

export const PLATFORM_POST_TYPE_MAP = {
  [PLATFORMS.INSTAGRAM]: {
    FEED: PLATFORM_POST_TYPE.INSTAGRAM_POST,
    STORY: PLATFORM_POST_TYPE.INSTAGRAM_STORY,
    REEL: PLATFORM_POST_TYPE.INSTAGRAM_REEL,
  },
  [PLATFORMS.FACEBOOK]: PLATFORM_POST_TYPE.FACEBOOK_POST,
  [PLATFORMS.TWITTER]: PLATFORM_POST_TYPE.TWITTER_POST,
  [PLATFORMS.PINTEREST]: PLATFORM_POST_TYPE.PINTEREST_PIN,
  [PLATFORMS.TIKTOK]: PLATFORM_POST_TYPE.TIKTOK_POST,
  [PLATFORMS.LINKEDIN]: PLATFORM_POST_TYPE.LINKEDIN_POST,
  [PLATFORMS.BLUESKY]: PLATFORM_POST_TYPE.BLUESKY_POST,
  [PLATFORMS.THREADS]: PLATFORM_POST_TYPE.THREADS_POST,
  [PLATFORMS.YOUTUBE]: PLATFORM_POST_TYPE.YOUTUBE_SHORT,
  [PLATFORMS.SNAPCHAT]: PLATFORM_POST_TYPE.SNAPCHAT_POST,
};

export const tabContexts = {
  FEED_POST: 'Feed Post',
  STORY: 'Story',
  REEL: 'Reel',
};

export const postStatus = {
  DRAFT: 'DRAFT',
  SCHEDULED: 'SCHEDULED',
  USERS_NOTIFIED: 'USERS_NOTIFIED',
  POSTED: 'POSTED',
  IMPORTED: 'IMPORTED',
  EXPIRED: 'EXPIRED',
  AUTOPUBLISHING: 'AUTOPUBLISHING',
  FAILED: 'FAILED',
  SKIPPED: 'SKIPPED',
  PARTIALLY_FAILED: 'PARTIALLY_FAILED',
  MISSING_APPROVALS: 'MISSING_APPROVALS',
};

export const carouselViewerAspectRatios = {
  SQUARE: 'square',
  PORTRAIT: 'portrait',
};

export const autoPublishErrors = {
  AUTO_PUBLISH_LIMIT_EXCEEDED: 'AUTO_PUBLISH_LIMIT_EXCEEDED',
  ACCOUNT_RESTRICTED: 'ACCOUNT_RESTRICTED',
  DUPLICATE_POST: 'DUPLICATE_POST',
  MISSING_AUTH: 'MISSING_AUTH',
  INVALID_AUTH: 'INVALID_AUTH',
  INVALID_IMAGE: 'INVALID_IMAGE',
  INTERNAL: 'INTERNAL',
  INVALID_PIN_BOARD: 'INVALID_PIN_BOARD',
  PAGE_PUBLISHING_AUTH_REQUIRED: 'PAGE_PUBLISHING_AUTH_REQUIRED',
  REJECTED: 'REJECTED',
  TEXT_LENGTH_EXCEEDED: 'TEXT_LENGTH_EXCEEDED',
  AGAINST_COMMUNITY_STANDARDS: 'AGAINST_COMMUNITY_STANDARDS',
  INVALID_ACCOUNT_TYPE: 'INVALID_ACCOUNT_TYPE',
  FLAGGED_AS_SPAM: 'FLAGGED_AS_SPAM',
  OUTSIDE_APPROVED_PUBLISHING_DATES: 'OUTSIDE_OF_APPROVED_PUBLISHING_DATES',
  APPROVED_PUBLISHING_DATES_HAVE_NO_OVERLAP: 'APPROVED_PUBLISHING_DATES_HAVE_NO_OVERLAP',
  INVALID_TARGETING: 'INVALID_TARGETING',
};

export const NoAccessToPage = (platformName) => ({
  PLAN_TITLE: "This feature isn't included in your plan",
  PLAN_MESSAGE: 'Contact your Customer Success Representative to try it out!',
  USER_MESSAGE: `You don't have the proper permissions enabled to schedule ${platformName} posts. Ask your Dash Social administrator to provide you access.`,
});

export const reconnectForMentionsMessage = (platformName) =>
  `You need to reconnect your ${platformName} account in Settings in order to tag followers`;

export const firstCommentStatus = {
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
};

export const videoStatus = {
  PROCESSING: 'PROCESSING',
  PROCESSING_FAILED: 'PROCESSING_FAILED',
  REVIEWING: 'REVIEWING',
  REVIEWING_FAILED: 'REVIEWING_FAILED',
  SUCCESS: 'SUCCESS',
};

export const popUpMessages = {
  removeFromScheduler: {
    title: 'Remove scheduled post?',
    message:
      'This will remove your post from your scheduled content. It will still remain in your library!',
    errorMessage:
      'Oops. We were not able to delete your post at this time. Please try again later.',
  },
};

export const removeApprovalRequestMessage = (name) => ({
  title: 'Remove Approval Request?',
  message: `This will remove ${name} as an approver of this post. Are you sure you want to proceed?`,
});

export const thumbnailSourceType = {
  VISION: 'VISION',
  SLIDER: 'SLIDER',
  CUSTOM: 'CUSTOM',
};

export const mixpanelMentionWarningType = {
  POST: 'post', // user tried adding more than allowed @mentions to a single tweet
  DAY: 'day', // user tried scheduling a tweet with @mentions and exceeded their 24h limit
};

export const mixpanelActions = {
  DUPLICATE_POST_CLICKED: 'Duplicate Post Clicked',
  LINK_UPDATE_CLICKED: 'Link Update Clicked',
  SCHEDULER_CALENDAR_OPENED: 'Scheduler Calendar Opened',
  INVITE_COLLABORATORS_CLICKED: 'Invite Collaborators Clicked',
};

export const adGeolocationFields = {
  COUNTRY: 'country',
  COUNTRY_GROUP: 'country_group',
};

// Detailed Slab doc about validations:
// https://dashhudson.slab.com/posts/scheduler-media-validations-wr26a2si
export const minRecommendedImageWidth = 1080;
export const minRecommendedVideoWidth = 600;

export const MAX_ALT_TEXT_LENGTH = 1000;
export const LINKEDIN_MAX_ALT_TEXT_LENGTH = 4086;

export const POST_EDITOR_SIDE_PANEL_WIDTH_KEY = '96';

export const mediaValidationMap = {
  // Instagram validations from https://developers.facebook.com/docs/instagram-api/reference/ig-user/media#image-specifications
  instagramFeed: {
    aspectRatio: {
      image: {
        min: '5:7',
        max: '1.91:1',

        minNumeric: 0.706,
        // Actual IG max is slightly more than 1.91:1
        maxNumeric: 1.92,
        platformName: 'Instagram',
      },
      video: {
        min: '5:7',
        max: '16:9',
        minNumeric: 0.706,
        maxNumeric: 1.78,
        platformName: 'Instagram',
      },
    },
    dimensions: {
      video: {
        maxWidth: '1920 pixels',
        maxWidthNumeric: 1920,
        maxHeight: '1920 pixels',
        maxHeightNumeric: 1920,
        platformName: 'Instagram',
      },
    },
    duration: {
      video: {
        min: '3 sec',
        max: '60 sec',
        minNumeric: 3,
        maxNumeric: 60,
        platformName: 'Instagram',
      },
    },
    fileSize: {
      image: {
        max: '8 MB',
        maxNumeric: 8 * 1024 * 1024,
        platformName: 'Instagram',
      },
      video: {
        max: '100 MB',
        maxNumeric: 100 * 1024 * 1024,
        platformName: 'Instagram',
      },
    },
  },
  instagramReel: {
    aspectRatio: {
      video: {
        min: '9:16',
        max: '16:9',
        minNumeric: 0.5625,
        maxNumeric: 1.78,
        platformName: 'Instagram',
      },
    },
    dimensions: {
      video: {
        maxWidth: '1920 pixels',
        maxWidthNumeric: 1920,
        platformName: 'Instagram',
      },
    },
    duration: {
      video: {
        min: '3 sec',
        max: '15 min',
        minNumeric: 3,
        maxNumeric: 900,
        platformName: 'Instagram',
      },
    },
    fileSize: {
      video: {
        max: '100 MB',
        maxNumeric: 100 * 1024 * 1024,
        platformName: 'Instagram',
      },
      coverImage: {
        max: '8 MB',
        maxNumeric: 8 * 1024 * 1024,
        platformName: 'Instagram',
      },
    },
  },
  instagramStory: {
    aspectRatio: {
      video: {
        min: '9:16',
        max: '16:9',
        minNumeric: 0.5625,
        maxNumeric: 1.78,
        platformName: 'Instagram',
      },
      image: {
        min: '9:16',
        max: '16:9',
        minNumeric: 0.5625,
        maxNumeric: 1.78,
        platformName: 'Instagram',
      },
    },
    dimensions: {
      video: {
        maxWidth: '1920 pixels',
        maxWidthNumeric: 1920,
        platformName: 'Instagram',
      },
    },
    duration: {
      video: {
        // Max is 60 seconds if feature flag for auto-publishing enabled
        // As validation only for auto-publish, changed here.
        // Limit not announced by Meta
        min: '3 sec',
        max: '60 sec',
        minNumeric: 3,
        maxNumeric: 60,
        platformName: 'Instagram',
      },
    },
    fileSize: {
      image: {
        max: '8 MB',
        maxNumeric: 8 * 1024 * 1024,
        platformName: 'Instagram',
      },
      video: {
        max: '50 MB',
        maxNumeric: 50 * 1024 * 1024,
        platformName: 'Instagram',
      },
    },
  },
  twitter: {
    // Limits from https://developer.twitter.com/en/docs/twitter-api/v1/media/upload-media/uploading-media/media-best-practices
    aspectRatio: {
      video: {
        min: '1:3',
        max: '3:1',
        minNumeric: 0.3,
        maxNumeric: 3,
        platformName: 'X',
      },
    },
    dimensions: {
      image: {
        min: '4 pixels',
        max: '8192 pixels',
        minNumeric: 4,
        maxNumeric: 8192,
        platformName: 'X',
      },
    },
    duration: {
      video: {
        // The max length is edited in utils/index.js
        // depending on whether the brand has the allowLongTwitterVideos featureflag
        min: '0.5 sec',
        minNumeric: 0.5,
        max: '140 sec',
        maxNumeric: 140,
        platformName: 'X',
      },
    },
    fileSize: {
      image: {
        max: '5 MB',
        maxNumeric: 5 * 1024 * 1024,
        platformName: 'X',
      },
      video: {
        max: '512 MB',
        maxNumeric: 512 * 1024 * 1024,
        platformName: 'X',
      },
    },
  },
  tiktok: {
    // Limits from https://bytedance.us.feishu.cn/docs/doccnq3SeHrZkncd0AdxL69MCEe#DMh0RO
    dimensions: {
      video: {
        min: '480 pixels',
        minNumeric: 480,
        platformName: 'TikTok',
      },
    },
    duration: {
      video: {
        // Allowance for 10 min videos was released on May 16th, though this is not reflected in the
        // TikTok API docs
        min: '3 sec',
        minNumeric: 3,
        max: '10 min',
        maxNumeric: 600,
        platformName: 'TikTok',
      },
    },
    fileSize: {
      video: {
        max: '1 GB',
        maxNumeric: 1024 * 1024 * 1024,
        platformName: 'TikTok',
      },
    },
  },
  pinterest: {
    // Limits from https://help.pinterest.com/en/business/article/pinterest-product-specs
    aspectRatio: {
      video: {
        min: '1:2',
        max: '1.91:1',
        minNumeric: 0.5,
        maxNumeric: 1.91,
        platformName: 'Pinterest',
      },
    },
    duration: {
      video: {
        min: '4 sec',
        minNumeric: 4,
        max: '15 min',
        maxNumeric: 900,
        platformName: 'Pinterest',
      },
    },
    fileSize: {
      image: {
        max: '32 MB',
        maxNumeric: 32 * 1024 * 1024,
        platformName: 'Pinterest',
      },
      video: {
        max: '2 GB',
        maxNumeric: 2 * 1024 * 1024 * 1024,
        platformName: 'Pinterest',
      },
    },
    dimensions: {
      image: {
        min: '100 pixels',
        minNumeric: 100,
        maxArea: '89.4 megapixels',
        maxAreaNumeric: 89478485, // (w * h)
        platformName: 'Pinterest',
      },
    },
  },
  facebook: {
    // Dimensions and aspect ratios to be permissive
    dimensions: {
      video: {
        min: '120 pixels',
        minNumeric: 120,
        platformName: 'Facebook',
      },
      image: {
        maxArea: '64,000,000 pixels',
        maxAreaNumeric: 8000 * 8000,
        platformName: 'Facebook',
      },
    },
    aspectRatio: {
      video: {
        min: '1:4',
        max: '4:1',
        minNumeric: 0.25,
        maxNumeric: 4,
        platformName: 'Facebook',
      },
    },
    duration: {
      video: {
        // Limits half of FB non-resumable limits (to avoid overwhelming DH)
        // https://developers.facebook.com/docs/video-api/guides/publishing#limitations-2
        min: '1 sec',
        minNumeric: 1,
        max: '10 min',
        maxNumeric: 600, // sec
        trimmerMax: '15 min',
        trimmerMaxNumeric: 900, // sec
        platformName: 'Facebook',
      },
    },
    fileSize: {
      image: {
        max: '4 MB',
        maxNumeric: 4 * 1024 * 1024, // In bytes
        platformName: 'Facebook',
      },
      video: {
        max: '500 MB',
        maxNumeric: 500 * 1024 * 1024, // In bytes
        platformName: 'Facebook',
      },
    },
  },
  linkedin: {
    // https://learn.microsoft.com/en-us/linkedin/marketing/community-management/shares/images-api
    // https://business.linkedin.com/marketing-solutions/success/ads-guide/video-ads
    dimensions: {
      image: {
        maxArea: '36152320 pixels',
        maxAreaNumeric: 36152320,
        platformName: PLATFORMS.LINKEDIN,
      },
      video: {
        min: '360 pixels',
        minNumeric: 360,
        maxWidth: '1920 pixels',
        maxWidthNumeric: 1920,
        maxHeight: '1920 pixels',
        maxHeightNumeric: 1920,
        platformName: 'LinkedIn',
      },
    },
    duration: {
      video: {
        min: '3 sec',
        minNumeric: 3,
        max: '30 min',
        maxNumeric: 1800,
        platformName: 'LinkedIn',
      },
    },
    aspectRatio: {
      video: {
        min: '9:16',
        max: '16:9',
        minNumeric: 0.5625,
        maxNumeric: 1.78,
        platformName: 'LinkedIn',
      },
    },
  },
};

// Platform post-types that support UTM tracking via UtmEditorPanel.vue
export const UTM_EDITOR_PANEL_SUPPORTED_PLATFORMS = [
  PLATFORM_POST_TYPE.FACEBOOK_POST,
  PLATFORM_POST_TYPE.PINTEREST_PIN,
  PLATFORM_POST_TYPE.TWITTER_POST,
  PLATFORM_POST_TYPE.INSTAGRAM_STORY,
];

export const defaultUtmEditorPanelState = () =>
  Object.fromEntries(UTM_EDITOR_PANEL_SUPPORTED_PLATFORMS.map((postType) => [postType, false]));

export const pinterestBoardEmptyMessage = 'Pin Board is required for auto-publishing.';
export const pinterestNoImageMessage = 'Please add one image to auto-publish.';
export const pinterestBadMediaRequirementsMessage =
  'For publishing these Pins, please make sure all media meet the requirement of content formats on Pinterest.';

// Limits from https://help.pinterest.com/en/business/article/pinterest-product-specs
export const pinterestCaptionLimit = {
  title: 100,
  description: 500,
};
export const pinterestMediaCountLimit = 25;
export const pinterestMediaCountLimitMessage = `You can only select up to ${pinterestMediaCountLimit} images at one time for bulk publishing.`;

export const pinterestCarouselPublishing = `Pinterest carousel publishing is not supported at this time. Please select one piece of media and try again.`;

export const pinterestRateLimitErrorPostStatusMessage =
  "This Pin could not be published. This can occur if you've exceeded the Pinterest API posting frequency limit or if the post was flagged as spam. Please reschedule your post and try again.";

export const somethingWentWrongTitle = 'Something Went Wrong';
export const somethingWentWrongMessage = `Sorry, it looks like something went wrong while saving your post.  Please try again.`;

export const shoppingTagsSubScreenTitle = 'Add Product Tags';
export const shoppingTagsSubScreenEmptyStateMessage = 'Click on media to tag products';

export const schedulerPostsSortTypes = {
  INDEX_DESC: '-SORT_INDEX',
  TIMESTAMP_ASC: 'TIMESTAMP',
  TIMESTAMP_DESC: '-TIMESTAMP',
};

export const postEditorSaveAction = {
  SAVE: 'saved',
  PUBLISH: 'published',
};

export const tagPeoplePromptMessage = 'Click on the photo to tag people';
export const tagPeopleVideoErrorMessage = 'You cannot tag people on videos';

export const maxUserTags = 20;
export const maxShoppingTagsPerMedia = 5;
export const maxDailyInstagramAutoPublishPosts = 100;

export const maxCollaborators = 5;
export const maxShoppingTagsPerPost = 20;
export const maxTagsPerMedia = Math.min(maxUserTags, maxShoppingTagsPerMedia);
export const maxTagsPerPost = Math.min(maxUserTags, maxShoppingTagsPerPost);

export const instagramRateLimitErrorPostStatusMessage = `You have reached the API limit of ${maxDailyInstagramAutoPublishPosts} auto published posts in 24hrs. Please publish this post via push notification in the Dash Social app.`;

export const tooManyUserTagsMessage = `This post can have a maximum of ${maxUserTags} tags, which include both user tags and collaborators. Please remove tags to continue.`;
export const tooManyShoppingTagsPerMediaMessage = `Only ${maxShoppingTagsPerMedia} product tags can be added per piece of media`;
export const tooManyShoppingTagsPerPostMessage = `Only ${maxShoppingTagsPerPost} total product tags can be added to a post`;
export const tooManyTagsPerMediaMessage = `Only ${maxTagsPerMedia} total people and product tags can be added per piece of media`;
export const tooManyTagsPerPostMessage = `Only ${maxTagsPerPost} total people and product tags can be added to a post`;
export const linkPromptMessage =
  'Cropping your media may have repositioned your people, product tags, and links. Please confirm their position before saving.';
export const mediaCropLinkTagsMessage =
  'People, product tags, and links may change position after cropping. They can be repositioned later.';
export const mediaCropLinkMessage =
  'Cropping your media may have repositioned your links. Please confirm their position before saving.';
export const mediaCropLinkTagsTitle = 'Cropping media may reposition tags and links';

export const eligibilityCallFailureNotificationMessage = 'Something Went Wrong';
export const eligibilityCallFailureNotificationSubtext =
  'This feature is currently unavailable due to an API error. Please try again later or if the issue persists, contact your Customer Success Representative.';

export const calendarDragReschedulingFailureNotificationMessage = 'Reschedule Failed';
export const calendarDragReschedulingFailureNotificationSubtext =
  'This post could not be rescheduled. Please try again. If the issue persists contact your Customer Success Representative.';

export const notEligibleMessageTitle = "You aren't eligible for Instagram Shopping";
export const notEligibleMessageContent =
  "You don't meet Facebook's Commerce Eligibility Requirements. This may be because you don't reside in a supported market or aren't compliant with Facebook's policies.";
export const instagramCommerceEligibilityRequirementsLink =
  'https://www.facebook.com/help/instagram/1627591223954487?helpref=faq_content';
export const instagramCarouselAspectRatioWarning =
  'Instagram will crop carousel cards to match the aspect ratio of the first card in the series. Please ensure all media has the same aspect ratio to avoid undesirable cropping and changed tag positions.';

export const enableInstagramShoppingAccessTitle = 'Enable Access to Instagram Shopping';
export const enableInstagramShoppingAccessMessage =
  'To add product tags to media you must authorize Dash Social to access Instagram Shopping.';
export const enableInstagramShoppingAccessButtonText = 'Connect Instagram Shopping';

export const tweetIsBeingSaved = 'Your post is currently being saved.';
export const tweetRemovedFromTimeline = 'Post removed from your timeline.';
export const tweetRemovedFromUnscheduled = 'Post removed from your unscheduled posts.';
export const tweetUpdatedAndAddedToTimeline = 'Post updated & added to your timeline.';
export const tweetAddedToTimeline = 'Post added to your timeline.';

export const twitterDailyMentionLimit = 100;

export const twitterMentionLimitExceededText = `You've already reached your limit of ${twitterDailyMentionLimit} Posts including @mentions within a 24-hour period. Please remove the @mention from your Tweet or schedule this for another time.`;
export const mediaLimitExceededText = 'You can only select up to 4 photos or a single video.';
export const tweetTooLongText = 'Your Post is too long.';
export const emptyTweetInThreadText = 'One or more of the Posts in your thread are empty.';
export const emptyTweetText = 'Your Post is empty.';
export const twitterNotConnectedText = 'To edit a post, you need to connect your X account.';
export const twitterLongFormTextNotPremium =
  "One of the selected brands doesn't have an X Premium account. Adjust your selection or stay within the 280 character limit.";

export const errorAltTextTooLong = (maxLength) =>
  `Your alt text must be ${maxLength.toLocaleString()} characters or less.`;
export const altTextErrorNotificationText =
  'Alt Text can not exceed 1,000 characters. Please shorten it and try again.';
export const altTextRemovedNotification = 'Alt text has been successfully removed.';
export const altTextSavedNotification = 'Alt text has been saved successfully.';

export const invalidShoppingTagsWarningMessage =
  'There is an issue with one or more of your product tags, please remove the tags to save your post.';
export const invalidCollaboratorsWarningMessage =
  "We were unable to include the following collaborators because they're invalid or private accounts";
export const invalidUserTagsWarningMessage =
  "We were unable to include the following user tags because they're invalid or private accounts";
export const invalidMediaPreviewTooltip =
  'Preview is unavailable because this media is invalid for this post type';
export const maxMediaLimitPreviewTooltip =
  "You've hit the maximum number of media allowed for this post. Please remove one or more files to proceed.";
export const publishDateRangeError = 'Outside of Approved Publishing Date';
export const publishDateOutOfRangeSubtext = 'Asset(s) can only be published between ';
export const noOverlapPublishDateSubtext = 'Asset(s) selected do not have an overlapping date.';

export const unknownApiErrorMessage =
  'Something went wrong saving your post. Please try again later.';

export const duplicatePostErrorMessage =
  'X posts with the same caption must be scheduled 24 hours apart';

export const noDuplicationForTwitterThreads = 'Duplication is not supported for Post threads.';

export const linkedinNotConnectedText =
  'To edit a post, you need to connect your LinkedIn account.';

export const youtubeRateLimitErrorPostStatusMessage =
  "This post did not go live because your channel hit YouTube's daily upload limit. All posts will be blocked from publishing until the 24 hour period is reset.";

export const draftOverdueBannerText =
  'This post could not be published because it is currently a Draft. Choose a new publication time and disable the Draft option.';

export const postSources = {
  SCHEDULED: 'Scheduled Post',
  UNSCHEDULED: 'Unscheduled Post',
  NEW: 'New Post',
};

export const mixpanelCRUDAction = {
  CREATED: 'Created',
  DELETED: 'Deleted',
  UPDATED: 'Updated',
};

export const postPublishType = {
  AUTO_PUBLISH: 'Auto Publish',
  SEND_NOTIFICATION: 'Send Notification',
};

export const UUID = Symbol('UUID');

export const ORIGINAL = Symbol('ORIGINAL');

export const inlineBannerInfo = Object.freeze({
  // The marketing team manages the QR code link, and Kevin helped set it up.
  // We now use qr-code-generator to create a redirect link, which will take users
  // to either the iOS App Store or Google Play Store depending on their device.
  QR_CODE_LINK: 'https://qrco.de/dash-social-app',
  LINK: {
    text: 'Learn More',
    url: 'https://help.dashsocial.com/hc/en-us/articles/14920389737357',
  },
  STORAGE_KEY: 'inlineSchedulerAppBanner',
  TEXT: 'The Dash Social mobile app is needed to <br> publish via Send Notification.',
});

export const postPublishInfoTooltip = {
  AUTO_PUBLISH:
    'Dash Social takes care of publishing your post automatically at the date and time you have selected.',
  SEND_NOTIFICATION:
    'The Dash Social mobile app will send a push notification when it’s time to post. You can proceed to publish the post manually by tapping the notification.',
  TIKTOK_CAPTION:
    'Your caption will copy to your mobile device’s clipboard when you tap “Open in TikTok” at the scheduled time to post.',
  IG_REEL_CAPTION:
    'Your caption will copy to your mobile device’s clipboard when you tap “Publish Now” at the scheduled time to post.',
  IG_FEED_CAPTION:
    'Your caption will copy to your mobile device’s clipboard when you tap “Open in Instagram” at the scheduled time to post.',
  FIRST_COMMENT:
    'Use the first comment on your post to add additional hashtags or extend the post’s caption.',
  ADD_NOTE:
    'Use this space to draft copy for your Story. You can manually copy and paste the text when it’s time for your post to go live.',
  LINK_STICKER:
    'Your link will automatically copy to your clipboard when you tap “Publish Now” at the scheduled time to post.',
};

export const schedulerFAQLink = 'https://help.dashsocial.com/hc/en-us/articles/14919531348749';

export const SCHEDULER_FILTERS = {
  FILTER_PLATFORM: 'scheduler_platform',
};

export const SCHEDULER_PLATFORM_FILTER_WIDGET = {
  TIKTOK: {
    title: 'TikTok',
    iconName: 'tiktok',
    value: 'TIKTOK',
  },
  INSTAGRAM: {
    title: 'Instagram',
    iconName: 'instagram',
    value: 'INSTAGRAM',
  },
  INSTAGRAM_STORY: {
    title: 'Instagram Stories',
    iconName: 'instagramStories',
    value: 'INSTAGRAM_STORY',
  },
  FACEBOOK: {
    title: 'Facebook',
    iconName: 'facebook',
    value: 'FACEBOOK',
  },
  PINTEREST: {
    title: 'Pinterest',
    iconName: 'pinterest',
    value: 'PINTEREST',
  },
  TWITTER: {
    title: 'X',
    iconName: 'twitter',
    value: 'TWITTER',
  },
  LINKEDIN: {
    title: 'LinkedIn',
    iconName: 'linkedin',
    value: 'LINKEDIN',
  },
  BLUESKY: {
    title: 'Bluesky',
    iconName: 'bluesky',
    value: 'BLUESKY',
  },
  THREADS: {
    title: 'Threads',
    iconName: 'threads',
    value: 'THREADS',
  },
  YOUTUBE: {
    title: 'YouTube',
    iconName: 'youtube',
    value: 'YOUTUBE',
  },
  SNAPCHAT: {
    title: 'Snapchat',
    iconName: 'snapchat',
    value: 'SNAPCHAT',
  },
};

export const FILTER_TYPE = {
  PLATFORM: 'Platform',
};

export const SCHEDULER_FILTER_WIDGETS = {
  scheduler_platform: {
    name: 'MediaFilterCheckboxWidget',
    label: 'Platform',
    noDuplicate: true,
    options: {
      filterOptions: Object.values(SCHEDULER_PLATFORM_FILTER_WIDGET).map((platform) => ({
        label: platform.title,
        formattedLabel: platform.title,
        icon: {
          channelIcon: true,
          name: platform.iconName,
        },
        value: platform.value,
      })),
    },
    type: FILTER_TYPE.PLATFORM,
  },
};

export const SCHEDULER_ICON_NAMES = {
  TIKTOK: 'tiktok',
  INSTAGRAM: 'instagram',
  INSTAGRAM_STORY: 'instagramStories',
  FACEBOOK: 'facebook',
  PINTEREST: 'pinterest',
  TWITTER: 'twitter',
  LINKEDIN: 'linkedin',
  SNAPCHAT: 'snapchat',
};

export const USER_TAG_TYPE = {
  MEDIA_TAG: 'mediaTag',
  COLLABORATOR: 'collaborator',
};

export const SCHEDULER_FILTER_PLATFORM = [
  {
    label: '',
    field: SCHEDULER_FILTERS.FILTER_PLATFORM,
    imgFilter: true,
    emptyOperator: true,
    valueToIconMap: SCHEDULER_ICON_NAMES,
    value: Object.keys(SCHEDULER_ICON_NAMES),
    emptyStates: false,
    type: FILTER_TYPE.PLATFORM,
  },
];

export const SCHEDULER_ADD_POST_DROPDOWN_OPTIONS = [
  {
    text: 'TikTok',
    iconName: 'tiktok',
    action: 'tiktok',
  },
  {
    text: 'Instagram Post',
    iconName: 'instagram',
    action: 'instagram',
  },
  {
    text: 'Facebook Post',
    iconName: 'facebook',
    action: 'facebook',
  },
  {
    text: 'Pinterest Single Pin',
    iconName: 'pinterest',
    action: 'pinterest',
  },
  {
    text: 'Pinterest Multiple Pins',
    iconName: 'pinterest',
    action: 'pinterestBulk',
  },
  {
    text: 'X Post',
    iconName: 'twitter',
    action: 'twitter',
  },
  {
    text: 'LinkedIn Post',
    iconName: 'linkedin',
    action: 'linkedin',
  },
];

export const SCHEDULER_ADD_NEW_OPTIONS_INSTAGRAM = [
  {
    label: 'Instagram Post',
    icon: 'instagram-color',
    action: 'instagram',
    postType: PLATFORM_POST_TYPE.INSTAGRAM_POST,
  },
  {
    label: 'Instagram Reel',
    icon: 'instagram-reel-color',
    action: 'instagram.reel',
    postType: PLATFORM_POST_TYPE.INSTAGRAM_REEL,
  },
  {
    label: 'Instagram Story',
    icon: 'instagram-story-color',
    action: 'instagram.story',
    postType: PLATFORM_POST_TYPE.INSTAGRAM_STORY,
  },
];

export const SCHEDULER_ADD_NEW_OPTIONS_TIKTOK = [
  {
    label: 'TikTok',
    icon: 'tiktok-color',
    action: 'tiktok',
    postType: PLATFORM_POST_TYPE.TIKTOK_POST,
  },
];
export const SCHEDULER_ADD_NEW_OPTIONS_FACEBOOK = [
  {
    label: 'Facebook Post',
    icon: 'facebook-color',
    action: 'facebook',
    postType: PLATFORM_POST_TYPE.FACEBOOK_POST,
  },
];
export const SCHEDULER_ADD_NEW_OPTIONS_PINTEREST = [
  {
    label: 'Pinterest Single Pin',
    icon: 'pinterest-color',
    action: 'pinterest',
    postType: PLATFORM_POST_TYPE.PINTEREST_PIN,
  },
  {
    label: 'Pinterest Multiple Pins',
    icon: 'pinterest-color',
    action: 'pinterestBulk',
  },
];
export const SCHEDULER_ADD_NEW_OPTIONS_X = [
  {
    label: 'X Post',
    icon: 'twitter-color',
    action: 'twitter',
    postType: PLATFORM_POST_TYPE.TWITTER_POST,
  },
];
export const SCHEDULER_ADD_NEW_OPTIONS_LINKEDIN = [
  {
    label: 'LinkedIn Post',
    icon: 'linkedin-color',
    action: 'linkedin',
    postType: PLATFORM_POST_TYPE.LINKEDIN_POST,
  },
];

export const SCHEDULER_ADD_NEW_OPTIONS_BLUESKY = [
  {
    label: 'Bluesky Post',
    icon: 'bluesky-color',
    action: 'bluesky',
    postType: PLATFORM_POST_TYPE.BLUESKY_POST,
  },
];

export const SCHEDULER_ADD_NEW_OPTIONS_THREADS = [
  {
    label: 'Threads Post',
    icon: 'threads-color',
    action: 'threads',
    postType: PLATFORM_POST_TYPE.THREADS_POST,
  },
];

export const SCHEDULER_ADD_NEW_OPTIONS_YOUTUBE = [
  {
    label: 'YouTube Short',
    icon: 'youtube-short-color',
    action: 'youtube',
    postType: PLATFORM_POST_TYPE.YOUTUBE_SHORT,
  },
];

export const SCHEDULER_ADD_NEW_OPTIONS_SNAPCHAT = [
  {
    label: 'Snapchat Post',
    icon: 'snapchat-color',
    action: 'snapchat',
    postType: PLATFORM_POST_TYPE.SNAPCHAT_POST,
  },
];

export const SCHEDULER_ADD_NEW_OPTIONS_EVENT = [
  { divider: true },
  {
    label: 'Event',
    icon: 'calendar-plus',
    action: 'event',
  },
];

export const SCHEDULER_ADD_NEW_OPTIONS_RSS_FEED = [
  {
    label: 'RSS Feed',
    icon: 'rss-feed',
    action: 'rssFeed',
  },
];

export const SCHEDULER_ADD_NEW_DROPDOWN_OPTIONS = [
  ...SCHEDULER_ADD_NEW_OPTIONS_INSTAGRAM,
  ...SCHEDULER_ADD_NEW_OPTIONS_TIKTOK,
  ...SCHEDULER_ADD_NEW_OPTIONS_FACEBOOK,
  ...SCHEDULER_ADD_NEW_OPTIONS_PINTEREST,
  ...SCHEDULER_ADD_NEW_OPTIONS_YOUTUBE,
  ...SCHEDULER_ADD_NEW_OPTIONS_LINKEDIN,
  ...SCHEDULER_ADD_NEW_OPTIONS_THREADS,
  ...SCHEDULER_ADD_NEW_OPTIONS_X,
  ...SCHEDULER_ADD_NEW_OPTIONS_BLUESKY,
  ...SCHEDULER_ADD_NEW_OPTIONS_SNAPCHAT,
  ...SCHEDULER_ADD_NEW_OPTIONS_EVENT,
  // TODO: use this when sc-164558 is done
  // ...SCHEDULER_ADD_NEW_OPTIONS_RSS_FEED,
];

export const CROSS_CHANNEL_POST_DETAILS = [
  {
    label: 'Instagram Post',
    value: PLATFORM_POST_TYPE.INSTAGRAM_POST,
    platform: PLATFORMS.INSTAGRAM,
    icon: 'instagram-color',
    iconMono: 'instagram-mono',
    name: 'instagram',
    props: {
      postType: postTypes.FEED,
    },
  },
  {
    label: 'Instagram Reel',
    value: PLATFORM_POST_TYPE.INSTAGRAM_REEL,
    platform: PLATFORMS.INSTAGRAM,
    icon: 'instagram-reel-color',
    iconMono: 'instagram-reel-mono',
    name: 'instagram-reel',
    props: {
      postType: postTypes.REEL,
    },
  },
  {
    label: 'Instagram Story',
    value: PLATFORM_POST_TYPE.INSTAGRAM_STORY,
    platform: PLATFORMS.INSTAGRAM,
    icon: 'instagram-story-color',
    iconMono: 'instagram-story-mono',
    name: 'instagram-story',
    props: {
      postType: postTypes.STORY,
    },
  },
  {
    label: 'TikTok',
    value: PLATFORM_POST_TYPE.TIKTOK_POST,
    platform: PLATFORMS.TIKTOK,
    icon: 'tiktok-color',
    iconMono: 'tiktok-mono',
    name: 'tiktok',
  },
  {
    label: 'Facebook Post',
    value: PLATFORM_POST_TYPE.FACEBOOK_POST,
    platform: PLATFORMS.FACEBOOK,
    icon: 'facebook-color',
    iconMono: 'facebook-mono',
    name: 'facebook',
  },

  {
    label: 'Pinterest Pin',
    value: PLATFORM_POST_TYPE.PINTEREST_PIN,
    platform: PLATFORMS.PINTEREST,
    icon: 'pinterest-color',
    iconMono: 'pinterest-mono',
    name: 'pinterest',
  },
  {
    label: 'YouTube Short',
    value: PLATFORM_POST_TYPE.YOUTUBE_SHORT,
    platform: PLATFORMS.YOUTUBE,
    icon: 'youtube-color',
    iconMono: 'youtube-mono',
    name: 'youtube',
    props: {
      platform: PLATFORMS.YOUTUBE,
      postType: PLATFORM_POST_TYPE.YOUTUBE_SHORT,
    },
  },
  {
    label: 'LinkedIn Post',
    value: PLATFORM_POST_TYPE.LINKEDIN_POST,
    platform: PLATFORMS.LINKEDIN,
    icon: 'linkedin-color',
    iconMono: 'linkedin-mono',
    name: 'linkedin',
  },
  {
    label: 'Threads Post',
    value: PLATFORM_POST_TYPE.THREADS_POST,
    platform: PLATFORMS.THREADS,
    icon: 'threads-color',
    iconMono: 'threads-mono',
    name: 'threads',
    props: {
      platform: PLATFORMS.THREADS,
      postType: PLATFORM_POST_TYPE.THREADS_POST,
    },
  },
  {
    label: 'X Post',
    value: PLATFORM_POST_TYPE.TWITTER_POST,
    platform: PLATFORMS.TWITTER,
    icon: 'x-color',
    iconMono: 'x-mono',
    name: 'x',
  },
  {
    label: 'Bluesky Post',
    value: PLATFORM_POST_TYPE.BLUESKY_POST,
    platform: PLATFORMS.BLUESKY,
    icon: 'bluesky-color',
    iconMono: 'bluesky-mono',
    name: 'bluesky',
    props: {
      platform: PLATFORMS.BLUESKY,
      postType: PLATFORM_POST_TYPE.BLUESKY_POST,
    },
  },
  {
    label: 'Snapchat Post',
    value: PLATFORM_POST_TYPE.SNAPCHAT_POST,
    platform: PLATFORMS.SNAPCHAT,
    icon: 'snapchat-color',
    iconMono: 'snapchat-mono',
    name: 'snapchat',
    props: {
      platform: PLATFORMS.SNAPCHAT,
    },
  },
];

export const DATE_RESTRICTION_ERRORS = {
  APPROVED_PUBLISHING_DATES_HAVE_NO_OVERLAP: 'APPROVED_PUBLISHING_DATES_HAVE_NO_OVERLAP',
  OUTSIDE_OF_APPROVED_PUBLISHING_DATES: 'OUTSIDE_OF_APPROVED_PUBLISHING_DATES',
};

export const EXPIRED_PUBLISHING_DATES_SCHEDULED_TIME_ERROR =
  'The approved publishing dates for media have expired';

export const INCOMPATIBLE_PUBLISHING_DATES_SCHEDULED_TIME_ERROR =
  'Media selected has incompatible approved date ranges';

export const OUTSIDE_PUBLISHING_DATES_SCHEDULED_TIME_ERROR =
  'Scheduled media is not approved to publish on this date';

export const CROSS_DUPLICATE_POST_TO_BRAND_KEYS = [
  'altTextMediaMap',
  'autoPublish',
  'location',
  'locationId',
  'media',
  'mediaIds',
  'message',
  'platform',
  'tweetStatus',
  'text',
  'video_title',
  'thumb_offset',
  'targeting',
  'subtitleContent',
  'subtitleMetadata',
];

export const CROSS_BRAND_DUPLICATE_ERROR =
  "Your post didn't duplicate successfully. Please return to the original post and try again.";

export const FACEBOOK_SUCCESSFUL_PUBLISH_WITH_TARGETING_MESSAGE =
  'Facebook post successfully published to selected location(s). Only Facebook page admins can view this post outside of the chosen location(s).';

export const INSTAGRAM_COLLABORATOR_INFORMATION_BANNER_TEXT = `Invite up to ${maxCollaborators} users to collaborate on this post. Once accepted, this post will appear on each collaborator's Feed or Reels, sharing metrics like comments and likes.`;

export const APPROVAL_POLICY = {
  APPROVAL_REQUIRED: 'APPROVAL_REQUIRED',
  APPROVAL_NOT_REQUIRED: 'APPROVAL_NOT_REQUIRED',
};

export const APPROVAL_STATUS = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  UNASSIGNED: 'UNASSIGNED',
};

export const PUBLISHING_APPROVALS_OVERRIDE_BANNER_LOCK =
  'This post is currently unlocked. Lock this post to require approval before it goes live.';
export const PUBLISHING_APPROVALS_OVERRIDE_BANNER_UNLOCK =
  'This post is currently locked. Unlock to enable this post to go live without approvals.';

export const APPROVALS_BANNER = {
  APPROVAL_NOT_REQUIRED:
    'This post will be published at the scheduled time as its approval policy is currently unlocked.',
  APPROVAL_REQUIRED:
    'This post needs approval before publishing. If not approved by the scheduled time, it will not publish and must be rescheduled.',
};

export const APPROVAL_REQUIRED_BANNER_MESSAGE =
  'These posts require approval before publishing. Please add reviewers in the post editor. Without approval, these posts will not go live and must be rescheduled.';

export const SCHEDULER_APPROVAL_REQUEST_RESOURCE_TYPE = {
  INSTAGRAM: 'SCHEDULER_INSTAGRAM_APPROVAL_REQUEST',
  PINTEREST: 'SCHEDULER_PINTEREST_APPROVAL_REQUEST',
  FACEBOOK: 'SCHEDULER_FACEBOOK_APPROVAL_REQUEST',
  TIKTOK: 'SCHEDULER_TIKTOK_APPROVAL_REQUEST',
  TWITTER: 'SCHEDULER_TWITTER_APPROVAL_REQUEST',
  LINKEDIN: 'SCHEDULER_LINKEDIN_APPROVAL_REQUEST',
  BLUESKY: 'SCHEDULER_BLUESKY_APPROVAL_REQUEST',
  THREADS: 'SCHEDULER_THREADS_APPROVAL_REQUEST',
  YOUTUBE: 'SCHEDULER_YOUTUBE_APPROVAL_REQUEST',
  SNAPCHAT: 'SCHEDULER_SNAPCHAT_APPROVAL_REQUEST',
};

export const SCHEDULER_REQUEST_APPROVED_RESOURCE_TYPE = {
  INSTAGRAM: 'SCHEDULER_INSTAGRAM_POST_APPROVED',
  PINTEREST: 'SCHEDULER_PINTEREST_POST_APPROVED',
  FACEBOOK: 'SCHEDULER_FACEBOOK_POST_APPROVED',
  TIKTOK: 'SCHEDULER_TIKTOK_POST_APPROVED',
  TWITTER: 'SCHEDULER_TWITTER_POST_APPROVED',
  LINKEDIN: 'SCHEDULER_LINKEDIN_POST_APPROVED',
  BLUESKY: 'SCHEDULER_BLUESKY_POST_APPROVED',
  THREADS: 'SCHEDULER_THREADS_POST_APPROVED',
  YOUTUBE: 'SCHEDULER_YOUTUBE_POST_APPROVED',
  SNAPCHAT: 'SCHEDULER_SNAPCHAT_POST_APPROVED',
};

export const SCHEDULER_APPROVALS_NOTIFICATION_MESSAGES = {
  APPROVAL_REQUEST: 'requested your approval on a post',
  REQUEST_APPROVED: 'approved your post',
};

export const SCHEDULER_ACTION_NAMES = {
  CREATE_POST: 'createPost',
  UPDATE_POST: 'updatePost',
  DELETE_POST: 'deletePost',
  TOGGLE_SIDE_PANEL: 'toggleSidePanel',
  BATCH_CREATE_POST: 'batchCreatePost',
};

export const draftToggleDisabledToolTip = 'Select a date and time to schedule your post.';

export const MULTISELECT_UNSCHEDULED_CHANNEL_ITEMS = {
  INSTAGRAM_FEED: {
    label: 'Instagram Feed',
    icon: 'instagram-color',
    platform: 'instagram',
  },
  INSTAGRAM_REEL: {
    label: 'Instagram Reel',
    icon: 'instagram-reel-color',
    platform: 'instagram',
  },
  INSTAGRAM_STORY: {
    label: 'Instagram Story',
    icon: 'instagram-story-color',
    platform: 'instagram',
  },
  TIKTOK: {
    label: 'TikTok',
    icon: 'tiktok-color',
    platform: 'tiktok',
  },
  FACEBOOK: {
    label: 'Facebook',
    icon: 'facebook-color',
    platform: 'facebook',
  },
  PINTEREST: {
    label: 'Pinterest',
    icon: 'pinterest-color',
    platform: 'pinterest',
  },
  YOUTUBE_SHORT: {
    label: 'YouTube Short',
    icon: 'youtube-color',
    platform: 'youtube',
  },
  LINKEDIN: {
    label: 'LinkedIn',
    icon: 'linkedin-color',
    platform: 'linkedin',
  },
  THREADS: {
    label: 'Threads',
    icon: 'threads-color',
    platform: 'threads',
  },
  X: {
    label: 'X',
    icon: 'x-color',
    platform: 'twitter',
  },
  BLUESKY: {
    label: 'Bluesky',
    icon: 'bluesky-color',
    platform: 'bluesky',
  },
  SNAPCHAT: {
    label: 'Snapchat',
    icon: 'snapchat-color',
    platform: 'snapchat',
  },
};

export const MULTISELECT_CHANNEL_ITEMS = {
  INSTAGRAM_FEED: 'Instagram Feed',
  INSTAGRAM_REEL: 'Instagram Reel',
  INSTAGRAM_STORY: 'Instagram Story',
  TIKTOK: 'TikTok',
  FACEBOOK: 'Facebook',
  PINTEREST: 'Pinterest',
  YOUTUBE_SHORT: 'YouTube Short',
  LINKEDIN: 'LinkedIn',
  THREADS: 'Threads',
  X: 'X',
  BLUESKY: 'Bluesky',
  SNAPCHAT: 'Snapchat',
};

export const MULTISELECT_CHANNEL_ICONS = {
  TikTok: 'tiktok-color',
  'Instagram Feed': 'instagram-color',
  'Instagram Story': 'instagram-story-color',
  'Instagram Reel': 'instagram-reel-color',
  Facebook: 'facebook-color',
  Pinterest: 'pinterest-color',
  X: 'x-color',
  LinkedIn: 'linkedin-color',
};

export const MEDIA_PREVIEW_TABS = Object.freeze({
  MEDIA_UPLOAD: 'mediaUpload',
  PREVIEW: 'preview',
});

export const PREVIEW_TAB_TOOLTIP = 'Preview how your post will appear when published';

export const PREVIEW_DISCLAIMER =
  'This is a preview of how your post will appear when published. Final look in-app may vary due to channel updates.';

export const SCHEDULER_DRAWERS = Object.freeze({
  EDIT_POST_DRAWER: 'schedulerEditPostDrawer',
  BULK_PINTEREST_DRAWER: 'schedulerBulkPinterestDrawer',
});

export const multiChannelAddChannelBannerInfo =
  'Deselecting a channel will discard your post for that channel. Any unsaved progress will be lost.';

export const multiChannelSyncBannerInfo =
  "Select Apply to All Posts to apply this post's media, publishing time and type, draft status, approval request and caption to all selected channels.";

export const multiChannelLearnMoreLink =
  'https://help.dashsocial.com/hc/en-us/articles/9335856964621-Creating-and-Duplicating-Posts-in-Scheduler';

export const multiBrandAndMultiChannelSyncBannerInfo =
  'Use Apply to All Posts to copy fields marked with the matching icon to all selected channels. Other applicable fields will be copied to all selected brands.';

export const defaultAutoPublishPreferencePerMessage =
  'Your publishing selection is remembered per channel and will be applied to your next post by default';

export const multiBrandAndMultiChannelSyncLearnMoreLink =
  'https://help.dashsocial.com/hc/en-us/articles/37021390095373';

export const addChannelDisabledForThreadedPosts =
  'Adding channels is unavailable when the primary post is threaded';

export const crossChannelDisableWhileMediaUploading =
  'This action is unavailable while media is uploading';

export const multiChannelSyncToolTip =
  'Customizations made to this field can be overwritten by clicking Apply to All Posts on the primary post';

export const crossChannelIconSelectorErrorToolTip =
  "There's a problem with this post preventing if from publishing. Open it to resolve the issue.";

export const crossChannelIconSelectorWarningToolTip =
  "There's an issue with this post. Open it for more details.";

export const fileUploadLimitExceededTooltip =
  'Your media exceeds the 1GB upload limit. Try a smaller file or compress it before uploading.';

export const thumbnailGenerationErrorMsg = (platform) =>
  `The thumbnail could not be saved for ${platform}. Open the post to adjust your selection and try again.`;

export const NEW_POST_ID = 'new';

export const invalidConnectionAutoPublish = (platform) =>
  `To auto publish a post, you need to connect your ${platform} account.`;

export const firstCommentPlaceholderText = 'Enter the first comment for your post';

export const crossBrandMediaDuplicationError =
  'Media could not be saved to all brands. Adjust your media selection or try again.';

export const EVENT_COLOR_OPTIONS = [
  { label: 'Black', value: 'eventBlack', class: 'dash-bg-event-black' },
  { label: 'Grey', value: 'eventGrey', class: 'dash-bg-event-grey' },
  { label: 'Denim Daydream', value: 'eventDenimDaydream', class: 'dash-bg-event-denim-daydream' },
  { label: 'Lucky Charm', value: 'eventLuckyCharm', class: 'dash-bg-event-lucky-charm' },
  { label: 'Polar Drift', value: 'eventPolarDrift', class: 'dash-bg-event-polar-drift' },
  { label: 'Plum Punch', value: 'eventPlumPunch', class: 'dash-bg-event-plum-punch' },
  { label: 'Rose Quartz', value: 'eventRoseQuartz', class: 'dash-bg-event-rose-quartz' },
];

export const DEFAULT_COLOR_CLASS = EVENT_COLOR_OPTIONS[0].class;

export const MAX_BRAND_SELECTED = 10; // overview filters
export const MAX_BRAND_SELECTED_INCREASED = 55; // overview filters
export const MAX_BRANDS_FOR_DUPLICATION = 20; // post editor
export const MAX_BRANDS_FOR_DUPLICATION_INCREASED = 55; // post editor

export const organizationAndReportingInfoTooltip =
  'Automatically segment and organize this post after publishing to reduce your overall reporting time';

export const addToAfterLabel = 'Organization and Reporting';

export const organizationAndReportingDisabledTooltip = `${addToAfterLabel} is not available for the selected post type`;

export const addToEmpty = 'Add Content Tags, Campaigns, Boards, and Galleries';

export const addToEmptyContentTagsOnly = 'Add Content Tags';

export const crossBrandDisabledTooltip =
  'Only uploaded media can be used when publishing to multiple brands. Adjust your media selection to add brands.';

export const crossBrandSelectorToolTip = {
  brandFilter: {
    noChannelAccess: 'You do not have channel access to this brand',
    platformDisconnected: (platform) => `This brand’s ${platform} account is disconnected`,
    noAutoPublishAccess: 'This brand does not have auto publishing permissions',
    defaultBrandSelected: 'Your current active brand is selected by default',
    notTwitterPremiumAccount: (brandName) => `${brandName} does not have an X premium account`,
  },
  brandTagFilter: {
    noChannelAccess: 'You do not have channel access for a brand in this brand tag',
    platformDisconnected: (platform) =>
      `A brand within this tag's ${platform} account is disconnected`,
    noAutoPublishAccess: 'A brand within this tag does not have auto publishing permissions',
    noFullBrandAccess: 'You do not have access to one or more brands in this brand tag',
    notTwitterPremiumAccount: 'A brand within this tag does not have an X Premium Account',
  },
};

export const crossBrandAutoPublishDisabledTooltip =
  'You do not have auto publish permissions for one or more brands that have been added to the post';

export const multiBrandSaveSuccessMessage =
  'You can edit brand-specific posts within the content calendar';

export const CROSS_BRAND_ALLOWED_MEDIA_SOURCE = [enumTypes.EDITOR, enumTypes.UPLOAD];

export const postCreationSource = {
  RSS_FEED: 'RSS_FEED',
};

export const threadsHashtagTooltip =
  'Use a hashtag to tag a topic. Only one topic can be tagged per caption.';

export const channelLimitedToMediaTypes = {
  [MULTISELECT_CHANNEL_ITEMS.TIKTOK]: [mediaTypes.VIDEO],
  [MULTISELECT_CHANNEL_ITEMS.INSTAGRAM_REEL]: [mediaTypes.VIDEO],
  [MULTISELECT_CHANNEL_ITEMS.YOUTUBE_SHORT]: [mediaTypes.VIDEO],
  [MULTISELECT_CHANNEL_ITEMS.BLUESKY]: [mediaTypes.IMAGE],
  [MULTISELECT_CHANNEL_ITEMS.THREADS]: [mediaTypes.IMAGE],
};

export const tiktokBrandedContentType = {
  PROMOTED: 'promoted',
  PAID_PARTNERSHIP: 'paid_partnership',
};

export const YOUTUBE_TITLE_MAX_LENGTH = 100;
export const YOUTUBE_DESCRIPTION_MAX_BYTES = 5000;

export const YOUTUBE_EDITOR_ERROR_MESSAGES = {
  TITLE_REQUIRED: 'Title is required for YouTube Shorts',
  TITLE_INVALID_CHARS: 'Title cannot include < or > characters.',
  TITLE_TOO_LONG: 'Title must be 100 characters or less.',
  DESCRIPTION_INVALID_CHARS: 'Description cannot include < or > characters.',
  DESCRIPTION_TOO_LONG: "Your description exceeds YouTube's length limit. Please shorten it.",
};

export const tiktokBrandedContentTooltip = {
  [tiktokBrandedContentType.PROMOTED]:
    "Apply the 'Promotional Content' label to this post. Use this when your video includes promo codes, links, product recommendations, or otherwise promotes your brand.",
  [tiktokBrandedContentType.PAID_PARTNERSHIP]:
    "Apply the 'Paid Partnership' label to this post. Use this when publishing user-generated content produced for your brand.",
};

// Calendar consts
export const RESTRICTED_PUBLISHING_TIME_TOOLTIP =
  'The selected time falls outside of the approved publishing dates';
// due to Terms of Service we cannot use custom metrics with YouTube.
export const YOUTUBE_IDEAL_TIME_TO_POST_TOOLTIP =
  'This is one of your best times to post based on average engagement.';
export const getIdealTimeToPostTooltip = (engagement) =>
  `This is one of your best times to post. Your average engagement at this time is <strong>${(engagement * 100).toFixed(2)}%</strong>.`;

export const SNAPCHAT_DESCRIPTION_MAX_LENGTH = 160;

export const SNAPCHAT_EDITOR_ERROR_MESSAGES = {
  DESCRIPTION_TOO_LONG: `Description must be ${SNAPCHAT_DESCRIPTION_MAX_LENGTH} characters or less.`,
};

export const SNAPCHAT_DISABLED_FIELDS_TOOLTIPS = {
  DESCRIPTION: 'Description is only available for Spotlights',
  SAVE_TO_PROFILE: 'Save to profile can only be applied for Spotlights',
  LANGUAGE: 'Language is only available for Spotlights',
};

export const SNAPCHAT_INFO_TOOLTIPS = {
  LANGUAGE: 'Select the language of your Spotlight',
};

export const SNAPCHAT_LOCALES = {
  DEFAULT: 'en_US',
  OPTIONS: [
    { label: 'Arabic', value: 'ar_AA' },
    { label: 'Bengali (Bangladesh)', value: 'bn_BD' },
    { label: 'Bengali (India)', value: 'bn_IN' },
    { label: 'Chinese (Simplified)', value: 'zh_CN' },
    { label: 'Chinese (Traditional)', value: 'zh_TW' },
    { label: 'Danish', value: 'da_DK' },
    { label: 'Dutch', value: 'nl_NL' },
    { label: 'English (UK)', value: 'en_GB' },
    { label: 'English (US)', value: 'en_US' },
    { label: 'Filipino', value: 'fil_PH' },
    { label: 'Finnish', value: 'fi_FI' },
    { label: 'French', value: 'fr_FR' },
    { label: 'German', value: 'de_DE' },
    { label: 'Greek', value: 'el_GR' },
    { label: 'Gujarati', value: 'gu_IN' },
    { label: 'Hindi', value: 'hi_IN' },
    { label: 'Indonesian', value: 'id_ID' },
    { label: 'Italian', value: 'it_IT' },
    { label: 'Japanese', value: 'ja_JP' },
    { label: 'Kannada', value: 'kn_IN' },
    { label: 'Korean', value: 'ko_KR' },
    { label: 'Malay', value: 'ms_MY' },
    { label: 'Malayalam', value: 'ml_IN' },
    { label: 'Marathi', value: 'mr_IN' },
    { label: 'Norwegian', value: 'nb_NO' },
    { label: 'Polish', value: 'pl_PL' },
    { label: 'Portuguese (Brazil)', value: 'pt_BR' },
    { label: 'Portuguese (Portugal)', value: 'pt_PT' },
    { label: 'Punjabi', value: 'pa_IN' },
    { label: 'Romanian', value: 'ro_RO' },
    { label: 'Russian', value: 'ru_RU' },
    { label: 'Spanish (Argentina)', value: 'es_AR' },
    { label: 'Spanish (International)', value: 'es_001' },
    { label: 'Spanish (Mexico)', value: 'es_MX' },
    { label: 'Spanish (Spain)', value: 'es_ES' },
    { label: 'Swedish', value: 'sv_SE' },
    { label: 'Tamil', value: 'ta_IN' },
    { label: 'Telugu', value: 'te_IN' },
    { label: 'Thai', value: 'th_TH' },
    { label: 'Turkish', value: 'tr_TR' },
    { label: 'Urdu', value: 'ur_PK' },
    { label: 'Vietnamese', value: 'vi_VN' },
  ],
};

// Generic mention regex for platforms that use simple @mention format (no encoding)
// Supports alphanumeric, dots, and underscores - works for Instagram, Bluesky, Threads
export const SIMPLE_MENTION_REGEX = /(?<!\w)@(\w[\w.]*)/g;
export const HASHTAG_REGEX = /#(\w+)/g;
export const EMAIL_REGEX = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/gi;
